import { MapPin, Layers, AlertTriangle, TrendingUp, Search, Radar, Loader2, Leaf } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import MapSearch from "@/components/MapSearch";
import { useCultureColors } from "@/lib/cultures";

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className="stat-card p-3 rounded-lg border border-black/5 bg-white" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#4A4A4A' }}>{label}</span>
    </div>
    <p className="text-xl font-bold tracking-tight" style={{ color: '#1A1A1A', fontFamily: 'Manrope, sans-serif' }}>{value}</p>
  </div>
);

const formatHa = (ha) => {
  if (ha >= 1000) return `${(ha / 1000).toFixed(1)}k ha`;
  return `${ha} ha`;
};

export const Sidebar = ({
  stats, communes, cultures, filterCommune, filterCulture,
  setFilterCommune, setFilterCulture, parcels, onSelectParcel, selectedParcelId,
  detecting, detectionResult, onRunDetection, activeLayer,
  isAdmin = false, onAddressSelect, onCadastreResults, onCreateFromCadastre, onParcelConverted,
}) => {
  const cultureColor = useCultureColors(); // couleurs du référentiel des cultures
  // Note: les parcelles sont affichées telles quelles (filtre commune / culture).
  // La recherche par adresse est gérée par le composant AddressSearch qui
  // déplace la carte plutôt que de filtrer les parcelles.
  const filteredParcels = parcels;

  return (
    <div className="floating-panel h-full flex flex-col overflow-hidden" data-testid="sidebar">
      {/* Header */}
      <div className="p-4 border-b border-black/5">
        <div className="flex items-center gap-2 mb-3">
          <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-8" />
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            <StatCard icon={Layers} label="Parcelles" value={stats.total_parcels} color="#2D6A4F" testId="stat-parcels" />
            <StatCard icon={TrendingUp} label="Surface" value={formatHa(stats.total_surface_ha)} color="#7EBC89" testId="stat-surface" />
            <StatCard icon={MapPin} label="Communes" value={Object.keys(stats.communes || {}).length} color="#E07A5F" testId="stat-communes" />
            <StatCard icon={Leaf} label="Cultures" value={Object.keys(stats.cultures || {}).length} color="#D4A373" testId="stat-cultures" />
          </div>
        )}

        {/* ML Detection — réservé à l'admin (le re-lancement ne change pas les résultats) */}
        {isAdmin && (
          <>
            <Button
              data-testid="sidebar-detection-btn"
              className="w-full h-9 rounded-full text-xs font-semibold gap-2"
              style={{ background: detecting ? '#E07A5F' : '#2D6A4F', color: 'white' }}
              onClick={onRunDetection}
              disabled={detecting}
            >
              {detecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />}
              {detecting ? "Analyse Sentinel-2 en cours..." : "Lancer Détection ML (admin)"}
            </Button>
            {detectionResult && (
              <div className="mt-2 p-2 rounded-lg text-[10px] border border-black/5" style={{ background: '#2D6A4F08' }}>
                <p className="font-semibold" style={{ color: '#2D6A4F' }}>
                  Dernière détection : {detectionResult.parcels_detected} parcelles ({detectionResult.total_surface_ha} ha)
                </p>
                <p className="mt-0.5" style={{ color: '#4A4A4A' }}>
                  Modèle : {detectionResult.model_version} | Source : {detectionResult.satellite_source}
                </p>
              </div>
            )}
          </>
        )}
        {activeLayer && (
          <p className="text-[9px] uppercase tracking-widest mt-2 text-center" style={{ color: '#4A4A4A' }}>
            Couche: {activeLayer === 'sentinel2' ? 'Sentinel-2' : activeLayer === 'esri' ? 'Satellite HD' : 'Carte'} | Source: RPG IGN officiel
          </p>
        )}
      </div>

      {/* Address search + filters */}
      <div className="p-4 space-y-2 border-b border-black/5">
        <MapSearch parcels={parcels} onAddressSelect={onAddressSelect} onSelectParcel={onSelectParcel}
          onCadastreResults={onCadastreResults} onCreateFromCadastre={onCreateFromCadastre} onParcelConverted={onParcelConverted} />
        <div className="flex gap-2">
          <Select value={filterCommune || "all"} onValueChange={(v) => setFilterCommune(v === "all" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs bg-white border-black/10" data-testid="filter-commune">
              <SelectValue placeholder="Commune" />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="all">Toutes communes</SelectItem>
              {communes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCulture || "all"} onValueChange={(v) => setFilterCulture(v === "all" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs bg-white border-black/10" data-testid="filter-culture">
              <SelectValue placeholder="Culture" />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="all">Toutes cultures</SelectItem>
              {cultures.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Parcel List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1.5">
          {filteredParcels.map((parcel) => (
            <button
              key={parcel.parcel_id}
              data-testid={`parcel-item-${parcel.parcel_id}`}
              onClick={() => onSelectParcel(parcel)}
              className={`w-full text-left p-3 rounded-lg transition-all duration-200 border ${
                selectedParcelId === parcel.parcel_id
                  ? 'border-[#E07A5F]/40 bg-[#E07A5F]/5'
                  : 'border-transparent hover:bg-black/[0.02] hover:border-black/5'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate" style={{ color: '#1A1A1A' }}>
                    {parcel.name}
                  </p>
                  <p className="text-xs mt-0.5 truncate" style={{ color: '#4A4A4A' }}>
                    {parcel.commune}{parcel.cirque ? ` (${parcel.cirque})` : ''} &middot; {parcel.surface_ha} ha
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0 max-w-[45%]">
                  <Badge
                    variant="secondary"
                    className="text-[10px] font-medium px-2 max-w-full overflow-hidden"
                    style={{
                      background: cultureColor(parcel.culture_type) + '15',
                      color: cultureColor(parcel.culture_type),
                      border: 'none'
                    }}
                  >
                    <span className="truncate">{parcel.culture_type}</span>
                  </Badge>
                  <span className="text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{
                      background: parcel.detection_method === 'rpg_officiel' ? '#2D6A4F15' : '#E07A5F15',
                      color: parcel.detection_method === 'rpg_officiel' ? '#2D6A4F' : '#E07A5F',
                    }}
                  >
                    {parcel.detection_method === 'rpg_officiel' ? 'RPG' : 'IA'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="h-1 flex-1 rounded-full bg-black/5">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(parcel.detection_confidence || 0) * 100}%`,
                      background: '#2D6A4F'
                    }}
                  />
                </div>
                <span className="text-[10px] font-medium shrink-0 tabular-nums" style={{ color: '#4A4A4A' }}>
                  {Math.round((parcel.detection_confidence || 0) * 100)}%
                </span>
              </div>
            </button>
          ))}
          {filteredParcels.length === 0 && (
            <p className="text-center text-sm py-8" style={{ color: '#4A4A4A' }}>
              Aucune parcelle trouvee
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default Sidebar;
